import React, {useMemo} from 'react';
import moment from 'moment';
import debounce from 'debounce';
import {usePagination, useTable} from 'react-table';
import {shallowEqual, useSelector} from 'react-redux';
import DataTableView from './DataTable-view';
import ButtonDropdownCommon from "../ButtonDropdownCommon";
import ButtonDropdownActions from '../ButtonDropdownActions';
import PopupEditTask from "../../../modules/Tasks/components/PopupEditTask";
import ButtonEditShuma from "../../../modules/Tasks/components/ButtonEditShuma";
import {taskTypeIDs as T} from "../../constants/taskTypes";
import {PopupEditTasksContextProvider} from "../../../modules/Tasks/components/PopupEditTask/popupEditTasksContext";
import {closeTask} from "../../../modules/Tasks/actions/closeTask";
import closeTaskIcon from "../../assets/check.svg";
import dueDateWarningPeriod from '../../../config/tasks.json';
import styles from "./styles.module.scss";
import StatusBadge from "../StatusBadge";
import {convertBinaryToString} from "../../utils/form/convertBinaryToString";

const DELAY = 450;

const DataTable = ({
                     data: tableData,
                     totalPages,
                     totalResultCount,
                     page,
                     setPage,
                     customClassNames = {},
                     columnsList,
                     showPagination = true,
                     columnStorageKey = null,
                     onColumnChange,
                     ActionButtonComponent = ButtonDropdownActions,
                     allowSorting = false,
                     onSortChange,
                     sortColumnName,
                     sortDirection,
                     refreshGrid,
                     loading,
                   }) => {
  const {currentUserID} = useSelector(
    (state) => ({
      currentUserID: state.user.userId,
    }),
    shallowEqual
  );

  const preparedData = tableData
    .map(item => {
      const {
        shumaID,
        firmShumaID,
        officialDate,
        address,
        gush,
        helka,
        cityName,
        streetName,
        houseNumber,
        shumaReasonName,
        shumaTypeName,
        propertyTypeName,
        customers,
        shumaStatusTypeID,
        shumaStatusTypeName,
        originTypeName,
        createTime,
        registeredArea,
        roomCount,
        userFirstName,
        userLastName,
        taskID,
        taskTypeID,
        taskType,
        taskStatus,
        taskStatusID,
        responsibleUserID,
        dueDate,
        saleDate,
        houseParking,
        buildingElevator,
        houseYard,
        houseStorage,
        houseRoof,
        ...rest
      } = item;

      const taskOpened = taskStatusID === 1;
      const taskClosed = taskStatusID === 2;
      const daysLeft = moment(dueDate).diff(moment(), 'days');
      const getDueDateColor = (styles) => {
        switch (true) {
          case (taskOpened && daysLeft > dueDateWarningPeriod):
            return {stylesDueDate: styles.openedMoreTime};
          case (taskOpened && (daysLeft <= dueDateWarningPeriod && daysLeft >= 0)):
            return {stylesDueDate: styles.openedLessTime};
          case (taskOpened && daysLeft < 0):
            return {stylesDueDate: styles.openedPassedTime};
          case taskClosed:
          default:
            return {stylesDueDate: styles.closed};
        }
      };
      const {stylesDueDate} = getDueDateColor(styles);

      const hideEditShumaButton = (responsibleUserID !== currentUserID)
        && (taskTypeID !== T.visit);

      return {
        shumaID,
        firmShumaID,
        shumaStatusTypeID,
        officialDate: (officialDate)
          ? moment(officialDate).format('DD-MM-YY')
          : '',
        saleDate: (saleDate)
          ? moment(saleDate).format('DD-MM-YY')
          : '',
        cityName,
        streetName,
        address: [
          <div>{cityName}</div>,
          <div>{address}</div>
        ],
        gush,
        helka,
        houseNumber,
        gushHelka: <div>{[gush, helka].filter(Boolean).join(' / ')}</div>,
        fullAddress: [
          <div>{cityName}</div>,
          <div>{[streetName, houseNumber].filter(Boolean).join(' - ')}</div>,
        ],
        houseParking: convertBinaryToString(houseParking),
        buildingElevator: convertBinaryToString(buildingElevator),
        houseYard: convertBinaryToString(houseYard),
        houseStorage: convertBinaryToString(houseStorage),
        houseRoof: convertBinaryToString(houseRoof),
        shumaAndPropertyTypes: [
          <div>{shumaTypeName}</div>,
          <div>{propertyTypeName}</div>
        ],
        shumaTypeName: [
          <div>{shumaReasonName}</div>,
          <div>{shumaTypeName}</div>
        ],
        responsibleUser: <div>{[userFirstName, userLastName].filter(Boolean).join(' ')}</div>,
        customers: customers && customers.map((item) => {
          return (
            <div className={styles.limitedWidthCell}>
              {item}
            </div>
          );
        }),
        createTime: [
          <div>{moment(createTime).format('DD-MM-YY')}</div>,
          <div>{originTypeName}</div>,
          <StatusBadge id={shumaStatusTypeID} name={shumaStatusTypeName}/>
        ],
        taskID: taskID,
        firmShumaIDAndPropertyType: [
          <div>{firmShumaID}</div>,
          <div>{propertyTypeName}</div>
        ],
        taskTypeAndTaskStatus: [
          <div>{taskType}</div>,
          <div>{taskStatus}</div>
        ],
        dueDate: (dueDate)
          ? (
            <span className={stylesDueDate}>
              {moment(dueDate).format('DD-MM-YY')}
            </span>
          )
          : '',
        tasksActions: (
          <ButtonDropdownCommon
            content={[
              (
                <PopupEditTasksContextProvider
                  taskID={taskID}
                  taskTypeID={taskTypeID}
                  shumaID={shumaID}
                  refreshGrid={refreshGrid}
                >
                  <PopupEditTask/>
                </PopupEditTasksContextProvider>
              ),
              !hideEditShumaButton && (
                <ButtonEditShuma shumaID={shumaID}/>
              ),
              taskOpened && (
                <div
                  onClick={() => {
                    closeTask(taskID);
                    refreshGrid();
                  }}
                  className={styles.closeTaskItem}
                >
                  <img src={closeTaskIcon} alt="close-task"/>
                  <span className={styles.closeTaskLabel}>סגירה</span>
                </div>
              )
            ]}
          />
        ),
        ...rest
      };
    })
    .map(item => {
      const {
        shumaID,
        firmShumaID,
        userID,
        shumaTypeID,
        originTypeID,
        shumaStatusTypeID,
        fileID,
        fileName,
        bankEmail
      } = item;

      return {
        ...item,
        actions: <ActionButtonComponent
          id={shumaID}
          firmShumaID={firmShumaID}
          userID={userID}
          shumaTypeID={shumaTypeID}
          originTypeID={originTypeID}
          shumaStatusTypeID={shumaStatusTypeID}
          fileID={fileID}
          fileName={fileName}
          bankEmail={bankEmail}
        />
      };
    });

  const data = useMemo(
    () => [...preparedData],
    [tableData]
  );
  const columns = useMemo(
    () => [...columnsList],
    []
  );
  const tableProps = useTable(
    {
      columns,
      data,
      initialState: {pageIndex: page}, // Pass our hoisted table state
      manualPagination: true, // Tell the usePagination
      // hook that we'll handle our own data fetching
      // This means we'll also have to provide our own
      // pageCount.
      pageCount: totalPages,
    },
    usePagination
  );

  const gotoPage = pageIndex => {
    setPage(pageIndex);
    tableProps.gotoPage(pageIndex);
  };

  const nextPage = debounce(() => {
    setPage(page + 1);
    tableProps.nextPage();
  }, DELAY);

  const previousPage = debounce(() => {
    setPage(page - 1);
    tableProps.previousPage();
  }, DELAY);

  const customTableProps = {
    ...tableProps,
    gotoPage,
    nextPage,
    pageIndex: page,
    previousPage,
    totalResultCount,
    customClassNames,
    showPagination,
    columnStorageKey,
    onColumnChange,
    allowSorting,
    onSortChange,
    sortColumnName,
    sortDirection,
    loading
  };

  return (
    <DataTableView {...customTableProps} />
  );
};

export default DataTable;
